/**
 * BodyMeasurementsTab.jsx
 * Inhalt des "Masse"-Tabs im Body-Tracking (Web-Portal / Mobile-Web).
 * Parität zur Mobile-App (06/2026):
 *  - Massband-Modus: Umfänge in cm (7 Standard-Stellen + eigene Messstellen)
 *  - Kaliper-Modus: 7 Jackson-Pollock-Hautfalten in mm + serverseitig
 *    berechnetes Körperfett % (jp7/jp3)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Info, Percent } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TAPE_SITES = [
  { key: 'chest', label: 'Brust' },
  { key: 'waist', label: 'Taille' },
  { key: 'hips', label: 'Hüfte' },
  { key: 'arm_left', label: 'Linker Arm' },
  { key: 'arm_right', label: 'Rechter Arm' },
  { key: 'thigh_left', label: 'Linker Oberschenkel' },
  { key: 'thigh_right', label: 'Rechter Oberschenkel' },
];

const CALIPER_SITES = [
  { key: 'chest', label: 'Brust' },
  { key: 'midaxillary', label: 'Achsel' },
  { key: 'triceps', label: 'Trizeps' },
  { key: 'subscapular', label: 'Rücken' },
  { key: 'abdomen', label: 'Bauch' },
  { key: 'suprailiac', label: 'Hüfte' },
  { key: 'thigh', label: 'Oberschenkel' },
];

export const BodyMeasurementsTab = ({ portalToken }) => {
  const [data, setData] = useState(null);
  const [method, setMethod] = useState('tape'); // 'tape' | 'caliper'
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  const [customRows, setCustomRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!portalToken) return;
    try {
      const res = await axios.get(`${API}/portal/body/measurements`, {
        headers: { Authorization: `Bearer ${portalToken}` },
      });
      setData(res.data || {});
    } catch (e) {
      console.error('Masse laden fehlgeschlagen:', e);
    }
  }, [portalToken]);

  useEffect(() => { load(); }, [load]);

  const openModal = () => {
    const sites = method === 'tape' ? TAPE_SITES : CALIPER_SITES;
    const source = method === 'tape' ? (data || {}) : (data?.caliper_measurements || {});
    const initial = {};
    sites.forEach((s) => { initial[s.key] = source[s.key] ?? ''; });
    setForm(initial);
    if (method === 'tape') {
      const custom = data?.custom_measurements || {};
      setCustomRows(Object.entries(custom).map(([name, value]) => ({ name, value })));
    }
    setShowModal(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (method === 'tape') {
        TAPE_SITES.forEach((s) => {
          const v = parseFloat(String(form[s.key]).replace(',', '.'));
          if (!isNaN(v) && v > 0) payload[s.key] = v;
        });
        const custom = {};
        customRows.forEach((row) => {
          const name = (row.name || '').trim();
          const v = parseFloat(String(row.value).replace(',', '.'));
          if (name && !isNaN(v) && v > 0) custom[name] = v;
        });
        if (Object.keys(custom).length) payload.custom_measurements = custom;
      } else {
        const caliper = {};
        CALIPER_SITES.forEach((s) => {
          const v = parseFloat(String(form[s.key]).replace(',', '.'));
          if (!isNaN(v) && v > 0) caliper[s.key] = v;
        });
        if (Object.keys(caliper).length) payload.caliper_measurements = caliper;
      }
      if (!Object.keys(payload).length) {
        toast.error('Bitte mindestens einen Wert eintragen');
        return;
      }
      await axios.put(`${API}/portal/body/measurements`, payload, {
        headers: { Authorization: `Bearer ${portalToken}` },
      });
      toast.success('Masse gespeichert');
      setShowModal(false);
      await load();
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const unit = method === 'tape' ? 'cm' : 'mm';
  const custom = data?.custom_measurements || {};
  const caliper = data?.caliper_measurements || {};
  const bodyFat = data?.caliper_body_fat_percent;
  const bfMethod = data?.caliper_bf_method;

  const tile = (key, label, value) => (
    <div key={key} className="bg-zinc-800/50 rounded-xl p-3" data-testid={`measure-tile-${key}`}>
      <p className="text-zinc-400 text-xs truncate">{label}</p>
      <p className="text-white text-lg font-bold mt-1">
        {value != null && value !== '' ? `${value} ${unit}` : `- ${unit}`}
      </p>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Methoden-Umschalter */}
      <div className="flex gap-1 bg-zinc-800 rounded-full p-1">
        <button
          onClick={() => setMethod('tape')}
          data-testid="body-measure-method-tape"
          className={`flex-1 py-2 rounded-full text-sm font-medium ${
            method === 'tape' ? 'bg-yellow-500 text-black' : 'text-zinc-300'
          }`}
        >
          Massband (cm)
        </button>
        <button
          onClick={() => setMethod('caliper')}
          data-testid="body-measure-method-caliper"
          className={`flex-1 py-2 rounded-full text-sm font-medium ${
            method === 'caliper' ? 'bg-yellow-500 text-black' : 'text-zinc-300'
          }`}
        >
          Kaliper (mm)
        </button>
      </div>

      {method === 'caliper' && (
        <div
          className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/5 rounded-2xl p-4 border border-yellow-500/30 text-center"
          data-testid="body-caliper-bodyfat"
        >
          {bodyFat != null ? (
            <>
              <p className="text-zinc-400 text-xs">Körperfett (Kaliper)</p>
              <p className="text-white text-3xl font-extrabold mt-1">{bodyFat} %</p>
              <p className="text-zinc-500 text-xs mt-1">
                Jackson-Pollock · {bfMethod === 'jp7' ? '7 Falten' : '3 Falten'}
              </p>
            </>
          ) : (
            <>
              <Percent className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
              <p className="text-white text-sm font-semibold">Körperfett-Berechnung</p>
              <p className="text-zinc-400 text-xs mt-1 leading-relaxed">
                Mindestens 3 Falten messen: Brust, Bauch & Oberschenkel (Männer)
                bzw. Trizeps, Hüfte & Oberschenkel (Frauen) — oder alle 7.
              </p>
            </>
          )}
        </div>
      )}

      {/* Kacheln */}
      <div className="grid grid-cols-2 gap-2">
        {method === 'tape'
          ? TAPE_SITES.map((s) => tile(s.key, s.label, data?.[s.key]))
          : CALIPER_SITES.map((s) => tile(s.key, s.label, caliper[s.key]))}
      </div>

      {/* Eigene Masse (nur Massband) */}
      {method === 'tape' && Object.keys(custom).length > 0 && (
        <>
          <p className="text-zinc-500 text-xs font-semibold tracking-wide mt-2">EIGENE MASSE</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(custom).map(([name, value]) => tile(`custom-${name}`, name, value))}
          </div>
        </>
      )}

      {method === 'caliper' && (
        <div className="bg-blue-500/10 rounded-xl p-3 flex gap-2">
          <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-zinc-400 text-xs leading-relaxed">
            Tipp: Immer auf der rechten Körperseite messen, Falte 2-3x greifen
            und den Mittelwert eintragen.
          </p>
        </div>
      )}

      <button
        onClick={openModal}
        data-testid="body-measurements-edit-btn"
        className="w-full py-3 bg-yellow-500 text-black rounded-xl font-medium flex items-center justify-center gap-2"
      >
        <Pencil className="w-4 h-4" />
        {method === 'tape' ? 'Masse eintragen' : 'Kaliper-Messung eintragen'}
      </button>

      {/* Eingabe-Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-2xl p-5 w-full max-w-sm max-h-[85vh] overflow-y-auto">
            <h3 className="text-white font-bold text-lg mb-3">
              {method === 'tape' ? 'Körpermasse (cm)' : 'Kaliper-Messung (mm)'}
            </h3>
            <div className="space-y-3">
              {(method === 'tape' ? TAPE_SITES : CALIPER_SITES).map((s) => (
                <div key={s.key}>
                  <label className="text-zinc-400 text-xs block mb-1">
                    {s.label} ({unit})
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={form[s.key] ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, [s.key]: e.target.value }))}
                    data-testid={`measure-input-${s.key}`}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm"
                  />
                </div>
              ))}

              {method === 'tape' && (
                <>
                  <p className="text-zinc-500 text-xs font-semibold tracking-wide pt-1">
                    EIGENE MASSE
                  </p>
                  {customRows.map((row, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        type="text"
                        placeholder="z.B. Kopf"
                        value={row.name}
                        onChange={(e) =>
                          setCustomRows((rows) =>
                            rows.map((r, i) => (i === idx ? { ...r, name: e.target.value } : r))
                          )
                        }
                        className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm"
                      />
                      <input
                        type="number"
                        step="0.1"
                        placeholder="cm"
                        value={row.value}
                        onChange={(e) =>
                          setCustomRows((rows) =>
                            rows.map((r, i) => (i === idx ? { ...r, value: e.target.value } : r))
                          )
                        }
                        className="w-24 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm"
                      />
                      <button
                        onClick={() =>
                          setCustomRows((rows) => rows.filter((_, i) => i !== idx))
                        }
                        className="p-2 text-zinc-500 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => setCustomRows((rows) => [...rows, { name: '', value: '' }])}
                    data-testid="custom-mass-add-row"
                    className="text-yellow-500 text-sm flex items-center gap-1.5"
                  >
                    <Plus className="w-4 h-4" />
                    Eigenes Mass hinzufügen
                  </button>
                </>
              )}
            </div>

            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 bg-zinc-700 rounded-xl text-zinc-300"
              >
                Abbrechen
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="measure-save-btn"
                className="flex-1 py-3 bg-yellow-500 rounded-xl text-black font-medium disabled:opacity-50"
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BodyMeasurementsTab;
